// Copyright 2024 Sadiant Inc. All Rights Reserved. This software is subject to a license agreement. Unauthorized or unlicensed use is prohibited.
import { Injectable, Inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { environment } from '../../../environments/environment';

export interface ArticleOgData {
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  section?: string;
}

export interface SeoData {
  title?: string;
  description?: string;
  keywords?: string;
  /** Relative path e.g. '/facilities' OR full absolute URL. SeoService resolves it. */
  url?: string;
  /** Relative path e.g. '/og/sadiant-facilities-og.jpg' OR full absolute URL. */
  image?: string;
  type?: string;
  // Additional standard meta
  author?: string;
  robots?: string;
  // Canonical — relative or absolute, resolved the same way as url
  canonical?: string;
  // Geo tags
  geoRegion?: string;
  geoPlacename?: string;
  geoPosition?: string;
  ICBM?: string;
  // App store banners
  appleItunesApp?: string;
  googlePlayApp?: string;
  // Article-specific OG (for news detail pages)
  article?: ArticleOgData;
  // JSON-LD structured data (array of schema objects)
  jsonLd?: object[];
}

@Injectable({
  providedIn: 'root'
})
export class SeoService {

  /** Base origin read from the active environment — works across local / release / staging. */
  private readonly baseUrl = environment.siteUrl;

  constructor(
    private titleService: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document
  ) {}

  updateMetaTags(data: SeoData) {
    const title       = data.title       || 'Sadiant Health | On-Demand Nurse Staffing';
    const description = data.description || 'Connect healthcare facilities with qualified clinicians instantly.';
    const keywords    = data.keywords    || 'healthcare, staffing, clinicians, nursing, jobs';
    const image       = this.toAbsolute(data.image       || '/assets/images/sadiant-logo.png');
    const pageUrl     = this.toAbsolute(data.url         || '/');
    const canonicalUrl= this.toAbsolute(data.canonical   || data.url || '/');
    const type        = data.type        || 'website';
    const robots      = data.robots      || 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

    // ── Title ──────────────────────────────────────────────────────────────
    this.titleService.setTitle(title);

    // ── Standard Meta Tags ─────────────────────────────────────────────────
    this.meta.updateTag({ name: 'title',       content: title });
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({ name: 'keywords',    content: keywords });
    this.meta.updateTag({ name: 'robots',      content: robots });

    if (data.author) {
      this.meta.updateTag({ name: 'author', content: data.author });
    } else {
      this.meta.removeTag("name='author'");
    }

    // ── Canonical Link ─────────────────────────────────────────────────────
    this.setCanonical(canonicalUrl);

    // ── Open Graph ─────────────────────────────────────────────────────────
    this.meta.updateTag({ property: 'og:type',         content: type });
    this.meta.updateTag({ property: 'og:site_name',    content: 'Sadiant Health' });
    this.meta.updateTag({ property: 'og:url',          content: pageUrl });
    this.meta.updateTag({ property: 'og:title',        content: title });
    this.meta.updateTag({ property: 'og:description',  content: description });
    this.meta.updateTag({ property: 'og:image',        content: image });
    this.meta.updateTag({ property: 'og:image:width',  content: '1200' });
    this.meta.updateTag({ property: 'og:image:height', content: '630' });
    this.meta.updateTag({ property: 'og:locale',       content: 'en_US' });

    // ── Article OG (for news detail pages) ────────────────────────────────
    if (data.article) {
      if (data.article.publishedTime) {
        this.meta.updateTag({ property: 'article:published_time', content: data.article.publishedTime });
      }
      if (data.article.modifiedTime) {
        this.meta.updateTag({ property: 'article:modified_time', content: data.article.modifiedTime });
      }
      if (data.article.author) {
        this.meta.updateTag({ property: 'article:author', content: data.article.author });
      }
      this.meta.updateTag({ property: 'article:section', content: data.article.section || 'Company News' });
    } else {
      this.meta.removeTag("property='article:published_time'");
      this.meta.removeTag("property='article:modified_time'");
      this.meta.removeTag("property='article:author'");
      this.meta.removeTag("property='article:section'");
    }

    // ── Twitter Card ───────────────────────────────────────────────────────
    this.meta.updateTag({ name: 'twitter:card',        content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:site',        content: '@sadianthealth' });
    this.meta.updateTag({ name: 'twitter:title',       content: title });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image',       content: image });

    // ── Geo Tags ───────────────────────────────────────────────────────────
    if (data.geoRegion) {
      this.meta.updateTag({ name: 'geo.region',    content: data.geoRegion });
      this.meta.updateTag({ name: 'geo.placename', content: data.geoPlacename || 'Fort Worth' });
      this.meta.updateTag({ name: 'geo.position',  content: data.geoPosition  || '32.7555;-97.3308' });
      this.meta.updateTag({ name: 'ICBM',          content: data.ICBM         || '32.7555, -97.3308' });
    } else {
      this.meta.removeTag("name='geo.region'");
      this.meta.removeTag("name='geo.placename'");
      this.meta.removeTag("name='geo.position'");
      this.meta.removeTag("name='ICBM'");
    }

    // ── App Store Smart Banners ────────────────────────────────────────────
    if (data.appleItunesApp) {
      this.meta.updateTag({ name: 'apple-itunes-app', content: data.appleItunesApp });
    } else {
      this.meta.removeTag("name='apple-itunes-app'");
    }
    if (data.googlePlayApp) {
      this.meta.updateTag({ name: 'google-play-app', content: data.googlePlayApp });
    } else {
      this.meta.removeTag("name='google-play-app'");
    }

    // ── JSON-LD Structured Data ────────────────────────────────────────────
    this.setJsonLd(data.jsonLd || []);
  }

  // ── Private helpers ──────────────────────────────────────────────────────

  /**
   * Converts a relative path ('/about-us') to a fully-qualified URL using the
   * environment's siteUrl.  If the value is already absolute (starts with http),
   * it is returned unchanged — so callers can pass either format.
   */
  private toAbsolute(path: string): string {
    if (!path) return this.baseUrl + '/';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return this.baseUrl + (path.startsWith('/') ? path : '/' + path);
  }

  private setCanonical(absoluteUrl: string): void {
    const existing = this.document.head.querySelector('link[rel="canonical"]');
    if (existing) {
      existing.remove();
    }
    const link = this.document.createElement('link');
    link.setAttribute('rel', 'canonical');
    link.setAttribute('href', absoluteUrl);
    this.document.head.appendChild(link);
  }

  private setJsonLd(schemas: object[]): void {
    // Remove all previously injected JSON-LD scripts
    this.document.head.querySelectorAll('script.app-json-ld').forEach(s => s.remove());

    // Inject new schemas
    schemas.forEach(schema => {
      const script = this.document.createElement('script');
      script.type = 'application/ld+json';
      script.className = 'app-json-ld';
      script.text = JSON.stringify(schema, null, 2);
      this.document.head.appendChild(script);
    });
  }
}
